/* eslint-disable react/no-unused-prop-types */
/* eslint-disable no-unused-vars */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import flatMap from 'lodash/flatMap';
import uniq from 'lodash/uniq';
import dayjs from '../../../utils/dayjs';
import {
  removeManualAssignment,
  saveManualAssignments,
} from '../../../actions/manual-assignments';
import OverLabledTextBox from '../../OverLabledTextBox/OverLabledTextBox';
import { useFullRoster } from '../../../hooks/useFullRoster';
import { getActiveStudents } from '../../../selectors/instructor-gradebook';
import MLDateTimePicker from '../../MLDateTimePicker';
import MLRadioButtonGroup from '../../MLRadio/MLRadioButtonGroup';
import MLRadioButton from '../../MLRadio/MLRadioButton';
import MLTextField from '../../MLTextField';
import MLSelect from '../../MLSelect';
import MLButton from '../../MLButton';
import MLFullPageDialog from '../../MLFullPageDialog';
import MLDialog from '../../MLDialog/MLDialog';
import {
  DEFAULT_ASSIGNED_TO,
  GRADING_TYPE_MANUAL_ASSIGNMENTS_DROPDOWN,
  GRADEBOOK_GRADING_TYPES_GRADED,
  GRADEBOOK_GRADING_TYPES_EXTRA_CREDIT,
} from '../../../utils/constants';
import MLIcon from '../../MLIcon';
import { getGradebookCategoryOptionsList } from '../../../utils/util';
import { selectGradebookCategoriesAndFilterUngradedCategory } from '../../../selectors/categories';

import s from './ManualAssignments.module.css';
import mlTextFieldStyles from '../../MLTextField/MLTextField.module.css';
import RosterSearchWrapper from './Components/RosterSearchWrapper/RosterSearchWrapper';

function formatForMLDatePicker(date) {
  return dayjs(date).format('YYYY-MM-DD');
}
function getDate(date) {
  if (date) {
    return dayjs(date).toISOString();
  }
  return dayjs().toISOString();
}

class ManualAssignments extends Component {
  static propTypes = {
    assignment: PropTypes.object,
    save: PropTypes.func,
    fetchGradebook: PropTypes.func,
    fetchCourse: PropTypes.func,
    onClose: PropTypes.func,
    roster: PropTypes.array,
    gradebookCategories: PropTypes.array,
    showModal: PropTypes.bool,
    deleteManualAssignment: PropTypes.func,
    disableEnforceFocus: PropTypes.bool,
  };

  constructor(props) {
    super(props);
    this.createRefs();
    const currentDate = getDate();
    const gradebookCategoryOptions = getGradebookCategoryOptionsList(
      props.gradebookCategories,
    );
    this.state = {
      setInitialGrade: false,
      setExtraCreditScore: false,
      isInitialGradeDirty: false,
      isExtraCreditScoreDirty: false,
      showRestrictedAccessWarning: false,
      initialGrade: 100,
      extraCreditScore: 100,
      // Begin form validation
      hasTitleError: false,
      hasPointsPossibleError: false,
      hasAssignedStudentsError: false,
      hasInitialScoreError: false,
      // End form validation
      showRemoveColumnWarning: false,
      gradebookCategoryOptions,
      selectedStudents: [{ studentId: DEFAULT_ASSIGNED_TO }],
    };
    const assignment = props.assignment;
    if (assignment) {
      const studentExceptions = flatMap(
        assignment.Exceptions,
        (exception) => exception.user_ids,
      );
      const assignedStudents = studentExceptions.length
        ? uniq(studentExceptions)
        : [DEFAULT_ASSIGNED_TO];

      let entryDate = getDate(
        formatForMLDatePicker(assignment.assignment_due_at),
      );
      let pointsPossible = assignment.assigned_points;

      if (assignedStudents[0] !== DEFAULT_ASSIGNED_TO) {
        const exception = assignment.Exceptions[0]; // Must have exceptions if not assigned to everyone.
        const exceptionValue = exception.exception_value;
        entryDate = getDate(formatForMLDatePicker(exceptionValue.due_date));
        pointsPossible = exceptionValue.assigned_points;
      }
      if (!entryDate) {
        entryDate = currentDate;
      }

      this.state = {
        ...this.state,
        assignedStudents,
        isUpdate: true,
        assignmentId: assignment.id,
        pointsPossible,
        title: assignment.name,
        gradebookCategoryId: assignment.gradebook_category_id,
        gradingType: assignment.grading_type,
        entryDate,
      };
    } else {
      const defaultCategory = gradebookCategoryOptions.find(
        (cat) => cat.isDefault,
      );
      this.state = {
        ...this.state,
        pointsPossible: 100,
        entryDate: currentDate,
        assignedStudents: [DEFAULT_ASSIGNED_TO],
        isCreate: true,
        title: '',
        gradebookCategoryId: defaultCategory?.value,
        gradingType: GRADEBOOK_GRADING_TYPES_GRADED,
      };
    }
  }

  createRefs() {
    this.titleInputRef = React.createRef();
    this.titleWrapperRef = React.createRef();
    this.assignToRef = React.createRef();
    this.assignWrapperRef = React.createRef();
    this.pointsPossibleRef = React.createRef();
    this.initialScoreRef = React.createRef();
    this.extraCreditScoreRef = React.createRef();
  }

  handleSave = () => {
    const { onClose, save } = this.props;
    const {
      title,
      assignedStudents,
      pointsPossible,
      entryDate,
      initialGrade,
      setInitialGrade,
      extraCreditScore,
      setExtraCreditScore,
      gradebookCategoryId,
      gradingType,
      assignmentId,
      hasAssignedStudentsError,
      hasPointsPossibleError,
      hasInitialScoreError,
      hasExtraCreditScoreError,
      selectedStudents,
    } = this.state;

    const hasTitleError = !title;
    if (hasTitleError) {
      this.setState({ hasTitleError: true });
    }

    const isInvalid =
      hasTitleError ||
      hasAssignedStudentsError ||
      hasInitialScoreError ||
      hasPointsPossibleError ||
      hasExtraCreditScoreError;

    if (!isInvalid) {
      const dueDate = dayjs(entryDate);
      dueDate.set({ hour: 23, minute: 59, second: 59, millisecond: 999 });
      save({
        assignmentId,
        title,
        assignedStudents,
        pointsPossible,
        entryDate: dueDate,
        initialGrade,
        setInitialGrade,
        extraCreditScore,
        setExtraCreditScore,
        gradebookCategoryId,
        gradingType,
        selectedStudents,
      });
      onClose();
    } else {
      this.handleValidationInputScroll({
        hasTitleError,
        hasAssignedStudentsError,
        hasPointsPossibleError,
        hasInitialScoreError,
        hasExtraCreditScoreError,
      });
    }
  };

  handleRosterSelect = (value) => {
    this.setState({
      selectedStudents: value,
      hasAssignedStudentsError: !value.length,
    });
  };

  handleDelete = async () => {
    const { assignment, deleteManualAssignment } = this.props;

    await deleteManualAssignment(assignment);
    this.setState({ showRemoveColumnWarning: false });
  };

  handleValidationInputScroll({
    hasTitleError,
    hasAssignedStudentsError,
    hasPointsPossibleError,
    hasInitialScoreError,
    hasExtraCreditScoreError,
  }) {
    // Must validate from top to bottom for the best user experience
    if (hasTitleError) {
      if (this.titleWrapperRef.current) {
        this.titleWrapperRef.current.scrollIntoView();
      }
      if (this.titleInputRef.current) {
        this.titleInputRef.current.focus();
      }
    } else if (hasAssignedStudentsError) {
      if (this.assignToRef.current) {
        this.assignWrapperRef.current.scrollIntoView();
        this.assignToRef.current.focus();
      }
    } else if (hasPointsPossibleError) {
      if (this.pointsPossibleRef.current) {
        this.pointsPossibleRef.current.focus();
      }
    } else if (hasInitialScoreError) {
      if (this.initialScoreRef.current) {
        this.initialScoreRef.current.focus();
      }
    } else if (hasExtraCreditScoreError) {
      if (this.extraCreditScoreRef.current) {
        this.extraCreditScoreRef.current.focus();
      }
    }
  }

  selectGradingType = (value) => {
    this.setState({ gradingType: parseInt(value, 10) });
  };

  updateSelectedStudents = (newSelectedStudents) =>
    new Promise((resolve) => {
      this.setState(
        {
          assignedStudents: newSelectedStudents,
          hasAssignedStudentsError: !newSelectedStudents.length,
        },
        () => resolve(),
      );
    });

  resetToEveryone = () =>
    new Promise((resolve) => {
      this.setState(
        {
          assignedStudents: [DEFAULT_ASSIGNED_TO],
          hasAssignedStudentsError: false,
        },
        () => {
          resolve();
        },
      );
    });

  renderGradingTypeDropdown = () => {
    const options = GRADING_TYPE_MANUAL_ASSIGNMENTS_DROPDOWN;
    const selectedGradingTypeIndex = options.findIndex(
      (type) => type.value === this.state.gradingType,
    );
    return (
      <div>
        <div className={s.topLabel}>Scoring</div>
        <MLSelect
          defaultOptionIndex={
            selectedGradingTypeIndex >= 0 ? selectedGradingTypeIndex : 0
          }
          testId="scoring-graded"
          options={options}
          onChange={this.selectGradingType}
        />
      </div>
    );
  };

  renderRadioOptions = () => {
    const {
      gradingType,
      hasInitialScoreError,
      hasExtraCreditScoreError,
      initialGrade,
      extraCreditScore,
    } = this.state;

    if (gradingType === GRADEBOOK_GRADING_TYPES_GRADED) {
      return (
        <div className={s.radioGroupWrapper}>
          <MLRadioButtonGroup
            key={GRADEBOOK_GRADING_TYPES_GRADED}
            className={s.gradeBookSettingsRadioGroup}
            defaultSelected="emptyScore"
            onChange={(e, value) => {
              if (value === 'setScore') {
                this.setState({
                  setInitialGrade: true,
                  hasInitialScoreError: !initialGrade || initialGrade < 0,
                });
              } else {
                this.setState({ setInitialGrade: false });
              }
            }}
          >
            <MLRadioButton
              value="setScore"
              className={s.settingsRadioBtnWithText}
              text="Default all grades to a default score:"
              testId="set_grade_default"
            >
              <MLTextField
                containerClassName={s.initialGrade}
                type="number"
                value={initialGrade}
                testId="grades-default-score"
                inputRef={this.initialScoreRef}
                hasError={hasInitialScoreError}
                errorMessages={['Score must be positive']}
                onChange={(e, value) => {
                  const newGradeInitialGrade = parseInt(value, 10);
                  if (isNaN(newGradeInitialGrade)) {
                    this.setState({
                      initialGrade: null,
                      isInitialGradeDirty: true,
                      hasInitialScoreError: true,
                    });
                  } else {
                    this.setState({
                      initialGrade: newGradeInitialGrade,
                      isInitialGradeDirty: true,
                      hasInitialScoreError: newGradeInitialGrade < 0,
                    });
                  }
                }}
              />
            </MLRadioButton>
            <MLRadioButton
              value="emptyScore"
              className={s.settingsRadioBtn}
              text="No default score."
              testId="no_default_grade"
            />
          </MLRadioButtonGroup>
        </div>
      );
    } else if (gradingType === GRADEBOOK_GRADING_TYPES_EXTRA_CREDIT) {
      return (
        <div className={s.radioGroupWrapper}>
          <MLRadioButtonGroup
            key={GRADEBOOK_GRADING_TYPES_EXTRA_CREDIT}
            className={s.gradeBookSettingsRadioGroup}
            defaultSelected="emptyExtraCreditScore"
            onChange={(e, value) => {
              if (value === 'setExtraCreditScore') {
                this.setState({
                  setExtraCreditScore: true,
                  hasExtraCreditScoreError:
                    !extraCreditScore || extraCreditScore < 0,
                });
              } else {
                this.setState({ setExtraCreditScore: false });
              }
            }}
          >
            <MLRadioButton
              value="setExtraCreditScore"
              className={s.settingsRadioBtnWithText}
              text="Default all students the same number of extra credit points:"
              testId="set_credit_default"
            >
              <MLTextField
                containerClassName={s.initialGrade}
                type="number"
                value={extraCreditScore}
                inputRef={this.extraCreditScoreRef}
                hasError={hasExtraCreditScoreError}
                errorMessages={['Score must be positive']}
                onChange={(e, value) => {
                  const newExtraCreditScore = parseInt(value, 10);
                  if (isNaN(newExtraCreditScore)) {
                    this.setState({
                      extraCreditScore: null,
                      isExtraCreditScoreDirty: true,
                      hasExtraCreditScoreError: true,
                    });
                  } else {
                    this.setState({
                      extraCreditScore: newExtraCreditScore,
                      isExtraCreditScoreDirty: true,
                      hasExtraCreditScoreError: newExtraCreditScore < 0,
                    });
                  }
                }}
              />
            </MLRadioButton>
            <MLRadioButton
              value="emptyExtraCreditScore"
              className={s.settingsRadioBtn}
              text="No default score."
              testId="no_default_score"
            />
          </MLRadioButtonGroup>
        </div>
      );
    }
    return null;
  };

  render() {
    const { roster, onClose, showModal, assignment, disableEnforceFocus } =
      this.props;
    const {
      isCreate,
      title,
      pointsPossible,
      entryDate,
      gradebookCategoryId,
      isInitialGradeDirty,
      showRestrictedAccessWarning,
      hasTitleError,
      hasPointsPossibleError,
      hasAssignedStudentsError,
      setInitialGrade,
      showRemoveColumnWarning,
      gradebookCategoryOptions,
    } = this.state;

    const gradebookCategoryIndex = gradebookCategoryOptions.findIndex(
      (cat) => cat.value === gradebookCategoryId,
    );

    return (
      <div data-test-id="gb-manual-assignments-dialog">
        <MLFullPageDialog
          show={showModal}
          title={isCreate ? 'Add Column' : 'Edit Activity Details'}
          actions={[
            <MLButton
              key="cancel"
              onClick={() => {
                this.setState({ showRestrictedAccessWarning: true });
              }}
              btnType="secondary"
              testId="btn_cancel"
            >
              Cancel
            </MLButton>,
            <MLButton
              key="save"
              btnType="primary"
              onClick={this.handleSave}
              testId="btn_save"
            >
              Save
            </MLButton>,
          ]}
        >
          <section className={s.formWrapper}>
            <h2
              className={s.assignmentDetailsHeader}
              ref={this.titleWrapperRef}
            >
              Assignment Details
            </h2>
            <OverLabledTextBox
              overLabelClassName={s.assignmentTitle}
              overLabelTxt="Assignment Title"
              testId="assignment-title-input"
              placeholder="Provide a title"
              inputRef={this.titleInputRef}
              hasError={hasTitleError}
              errorMessages={['Assignments must be named']}
              value={title}
              textFieldClassName={s.textOverTextBox}
              textFieldContainerClassName={s.textOverTextBoxContainer}
              onBlur={() => {
                this.setState({ hasTitleError: !title });
              }}
              onChange={(e, value) => {
                this.setState({ title: value, hasTitleError: !value });
              }}
            />
            <section ref={this.assignWrapperRef}>
              <span className={s.topLabel}>Assign to</span>
              <div className={s.rosterSearchContainer}>
                <RosterSearchWrapper
                  exceptions={assignment?.exceptions}
                  roster={roster}
                  courseId={this.props.courseId}
                  handleSelect={this.handleRosterSelect}
                />
              </div>
              {hasAssignedStudentsError && (
                <div className={mlTextFieldStyles.MLTextFieldErrorText}>
                  <span>Must assign a person or persons</span>
                </div>
              )}
            </section>
            <div>
              <span className={s.topLabel}>Due Date</span>
              <MLDateTimePicker
                boxed
                className={s.entryDate}
                pickerType="date"
                value={entryDate}
                testId="due-date"
                onChange={(e, value) => {
                  this.setState({ entryDate: getDate(value) });
                }}
                closeOnChange
              />
            </div>
            <div className={s.gradeOptions}>
              <OverLabledTextBox
                hasError={hasPointsPossibleError}
                errorMessages={['Activity must be worth at least 1 point']}
                overLabelTxt="Points possible"
                value={pointsPossible}
                testId="points-possible"
                inputRef={this.pointsPossibleRef}
                textFieldClassName={s.textOverTextBox}
                textFieldContainerClassName={s.pointsPossibleContainer}
                onChange={(e, value) => {
                  const newPointsPossible = parseInt(value, 10);
                  if (isInitialGradeDirty && !isNaN(newPointsPossible)) {
                    this.setState({ pointsPossible: newPointsPossible });
                  } else {
                    this.setState({
                      pointsPossible: newPointsPossible,
                      initialGrade: newPointsPossible,
                    });
                  }
                  const pointsPossibleError =
                    !newPointsPossible || newPointsPossible < 1;
                  this.setState({
                    hasPointsPossibleError: pointsPossibleError,
                    hasInitialScoreError:
                      pointsPossibleError && setInitialGrade,
                  });
                }}
                type="number"
              />
              {this.renderGradingTypeDropdown()}
            </div>
            {isCreate && (
              <div>
                <span className={s.scoreHeader}>Student scores</span>
                <p className={s.scoreDesc}>
                  Below you can set your students&#39; grade for the assignment
                  or leave it blank. You can always go back and edit each
                  student&#39;s grade after adding the assignment.
                </p>
                {this.renderRadioOptions()}
              </div>
            )}
            <div>
              <div className={s.topLabel}>Gradebook Category:</div>
              <MLSelect
                defaultOptionIndex={gradebookCategoryIndex}
                options={gradebookCategoryOptions}
                testId="gradebook-category"
                onChange={(value) => {
                  this.setState({
                    gradebookCategoryId: parseInt(value, 10),
                  });
                }}
              />
            </div>
            {!isCreate && (
              <div className={s.deleteButtonContainer}>
                <MLButton
                  btnType="secondaryRed"
                  testId="btn_delete_activity"
                  onClick={() =>
                    this.setState({ showRemoveColumnWarning: true })
                  }
                >
                  <MLIcon
                    className={s.deleteIcon}
                    type="trash"
                    title="Delete this activity"
                  />
                  <span>Delete this activity</span>
                </MLButton>
              </div>
            )}
          </section>
        </MLFullPageDialog>
        {
          <MLDialog
            show={showRestrictedAccessWarning}
            title="Confirm Navigation"
            onClose={() => {
              this.setState({ showRestrictedAccessWarning: false });
            }}
            actions={[
              <MLButton
                key="cancel_button"
                btnType="secondary"
                testId="cancel_button"
                onClick={() => {
                  this.setState({ showRestrictedAccessWarning: false });
                }}
              >
                Cancel
              </MLButton>,
              <MLButton
                key="leave_page_button"
                btnType="primary"
                type="submit"
                testId="btn_leave_page"
                onClick={() => {
                  onClose();
                  this.setState({ showRestrictedAccessWarning: false });
                }}
              >
                Leave Page
              </MLButton>,
            ]}
            disableEnforceFocus={disableEnforceFocus}
          >
            <p className={s.warningText}>
              Navigation away from this page will cause any unsaved work to{' '}
              <br /> be lost. Are you sure you want to leave?
            </p>
          </MLDialog>
        }

        {assignment && (
          <MLDialog
            show={showRemoveColumnWarning}
            title="Remove Column"
            actions={[
              <MLButton
                key="confirm_button"
                btnType="secondaryRed"
                type="submit"
                testId="btn_confirm"
                onClick={this.handleDelete}
              >
                Yes, Remove
              </MLButton>,
              <MLButton
                key="cancel_button"
                btnType="secondary"
                testId="cancel_button"
                onClick={() => {
                  this.setState({ showRemoveColumnWarning: false });
                }}
              >
                Cancel
              </MLButton>,
            ]}
            onClose={() => {
              this.setState({ showRemoveColumnWarning: false });
            }}
            disableEnforceFocus={disableEnforceFocus}
          >
            Are you sure you would like to remove the column &quot;
            {assignment.name}&quot; <br />
            and grades? This action cannot be undone.
          </MLDialog>
        )}
      </div>
    );
  }
}

function mapStateToProps(state) {
  return {
    courseId: state.gradebook.courseId,
    gradebookCategories:
      selectGradebookCategoriesAndFilterUngradedCategory(state),
  };
}

const mapDispatchToProps = (dispatch) => ({
  save: (assignment) => dispatch(saveManualAssignments(assignment)),
  deleteManualAssignment: (assignment) =>
    dispatch(removeManualAssignment(assignment)),
});

function ManualAssignmentsWithRoster(props) {
  const fullRoster = useFullRoster(props.courseId);
  return (
    <ManualAssignments {...props} roster={getActiveStudents(fullRoster)} />
  );
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(ManualAssignmentsWithRoster);
